import { useEffect, useReducer, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import DatatableBase from "Components/Datatable/DatatableBase";
import { Datatable_Column } from "Components/Datatable/DatatableComponents/DatatableColumn";
import { isInObject, applyDateSelectorFormat } from "Utils/utilityFunctions";
import { setGlobalErrorStatus, setGlobalSuccessStatus } from "Components/StatusHandling";
import { ERROR_MESSAGES } from "Utils/constants";
import { getDelistedPSOList, submitDelistedPSO } from "./ViewBatchAxiosFuncs";
import { TextButton } from "Components/Button";
import { Form, Stack } from "react-bootstrap";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import { ACTIONS, formInfoReducer } from "./DelistDateFormDataReducer";

const DelistedPSOTable = ({ batchId }) => {
  const dispatch = useDispatch();
  const loginInfo = useSelector((s) => s.loginInfo);
  const location = useLocation();
  
  const [filter, setFilter] = useState(null);
  const [sortColumn] = useState({ sortField: "processDate", sortDirection: "desc" });
  const [refreshTableData, setRefreshTableData] = useState(false);
  const [submitting, setSubmitting] = useState({});
  
  const initialState = {
    data: {},
    loading: false,
    error: null,
  };
  const [dateFormInfo, dateFormDispatch] = useReducer(formInfoReducer, initialState);
  
  const ensureField = (fieldName) => {
    if (!dateFormInfo.data?.[fieldName]) {
      dateFormDispatch({ type: ACTIONS.ADD_FIELD, name: fieldName });
    }
  };
  
  const handleDateChange = (fieldName, ymd) => {
    // normalize via applyDateSelectorFormat (keeps consistent across browsers)
    const val = applyDateSelectorFormat(ymd);
    dateFormDispatch({
      type: ACTIONS.DATE_CHANGE,
      name: fieldName,
      value: val,
    });
  };
  
  const dispErrors = (fieldName) => {
    const f = dateFormInfo.data?.[fieldName];
    if (!f) return null;
    const errs = f.validation?.errors ?? [];
    return (
      <>
        {errs.length > 0
          ? errs.map((errorText, index) => (
            <Stack
              key={`${fieldName}-Errors_${index}`}
              direction="horizontal"
              gap={2}
              className="align-items-start mt-1"
            >
              <div id="errorIcon">
                <ErrorIcon fontSize="small" />
              </div>
              <div className="small">{errorText}</div>
            </Stack>
          ))
          : null}
      </>
    );
  };
  
  function getFieldValue(fieldName, data) {
    const values = data[fieldName];
    if (!values || !values.value) return "";
    const split = String(values.value).split("-");
    if (split.length === 3) {
      return split[1] + "/" + split[2] + "/" + split[0];
    }
    return values.value;
  }
  
  function addErrorMessages(fieldName, errorFieldName, serverPayload, isFirst) {
    const d = serverPayload?.fieldErrors;
    const values = d?.[errorFieldName];
    if (!values) return;
    
    const err = values.errorMessages ?? [];
    if (isFirst === true) {
    }
    setGlobalErrorStatus({
      dispatch,
      popupData: {
        visible: true,
        title: ERROR_MESSAGES.DATA_ERROR.title,
        msg: err,
        showContactHelp: false
      },
      redirect: {redirect: false, redirectFirst: false, path: "", data: {}}
    });
    
    dateFormDispatch({
      type: ACTIONS.DATE_SET_ERRORS,
      name: fieldName,
      errors: null,
    });
  }
  
  function clearErrorMessages(keysObj) {
    for (const key of Object.keys(keysObj)) {
      dateFormDispatch({
        type: ACTIONS.DATE_REMOVE_ERRORS,
        name: key,
      });
    }
  }
  
  useEffect(() => {
    const passed = location.state;
    if (isInObject("filter", passed)) {
      setFilter(passed.filter);
    } else {
      setFilter({
        startIndex: 0,
        entryPerPage: 10,
        sortingColumn: sortColumn.sortField,
        sortingOrder: sortColumn.sortDirection,
        searchTerm: "",
      });
    }
  }, []);
  
  const handleProcessedDateFormat = (time) => {
    if (time == null) return "";
    const d = new Date(time);
    const pad2 = (n) => String(n).padStart(2, "0");
    return (
      `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()} ` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    );
  };
  
  const handleDelistDateFormat = (time) => {
    if (time == null) return "";
    const d = new Date(time);
    const pad2 = (n) => String(n).padStart(2, "0");
    return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()} `;
  };
  
  const showDelistedDate = (timestamp, row) => {
    if (row.orgStatus === "Inactive" && row.delistedDate != null) {
      return handleDelistDateFormat(timestamp);
    }
    return inputDateComponent(row);
  };
  
  const inputDateComponent = (row) => {
    const fieldName = String(row.delistedId);
    ensureField(fieldName);
    const field = dateFormInfo.data?.[fieldName];
    const value = field?.value ?? "";
    
    return (
      <>
        <Form.Group className="mb-0">
          <Form.Control
            type="date"
            size="sm"
            aria-label="Delist Date"
            value={value}
            onChange={(e) => handleDateChange(fieldName, e.target.value)}
            isInvalid={field?.validation?.isInvalid}
          />
        </Form.Group>
        {dispErrors(fieldName)}
      </>
    );
  };
  
  const showDelistAction = (_, row) => {
    return row.orgStatus !== "Inactive" && row.delistedDate == null
      ? delistButtonComponent(row)
      : "";
  };
  
  const delistButtonComponent = (row) => {
    const isSubmitting = !!submitting[row.delistedId];
    return (
      <TextButton
        className="ms-auto table-button-disable"
        onClick={() => {
          if (!isSubmitting) {
            handleDelistPSO(row);
          }
        }}
        variant="outline"
        content={"Submit"}
        disabled={isSubmitting}
      />
    );
  };
  
  const getData = async (flt) => {
    try {
      return await getDelistedPSOList("Bearer " + loginInfo.token, flt, batchId);
    } catch (error) {
      const payload = error?.data ?? error;
      setGlobalErrorStatus({
        dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: payload?.data?.globalErrorMessageList?.[0] ?? ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true,
        },
        redirect: { redirect: false, redirectFirst: false, path: "", data: {} },
        error,
      });
      return { status: -1, data: { totalCount: 0, entryList: [] } };
    }
  };
  
  const handleDelistPSO = async (row) => {
    const fieldName = String(row.delistedId);
    ensureField(fieldName);
    
    clearErrorMessages({ [fieldName]: {} });
    
    const stored = dateFormInfo.data?.[fieldName];
    const ymd = stored?.value;
    if (!ymd) {
      setGlobalErrorStatus({
        dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: "Please select a Delisting Date to submit.",
          showContactHelp: false
        },
        redirect: {redirect: false, redirectFirst: false, path: "", data: {}}
      });
      return;
    }
    
    const delistDateString = getFieldValue(fieldName, dateFormInfo.data);
    
    const payload = {
      batchId: row.batchId,
      psoId: row.psoId,
      delistDateString: delistDateString,
    };
    
    try {
      setSubmitting((prev) => ({ ...prev, [row.delistedId]: true }));
      const res = await submitDelistedPSO("Bearer " + loginInfo.token, payload);
      const response = res?.data ?? res;
      
      setGlobalSuccessStatus({
        dispatch,
        popupData: {
          visible: true,
          title: "Success!",
          msg: response?.message ?? "Delist submission completed.",
          showContactHelp: false,
        },
        redirect: { redirect: false, redirectFirst: false, path: "", data: {} },
      });
      setRefreshTableData(true);
      setFilter((f) => ({ ...f }));
    }
    catch (error) {
      setSubmitting((prev) => ({ ...prev, [row.delistedId]: false }));
      const payloadErr = error?.data ?? error;
      
      const hasFieldErr =
        payloadErr?.data?.fieldErrors && payloadErr?.data?.fieldErrors?.delistDateString;
      
      if (hasFieldErr) {
        addErrorMessages(fieldName, "delistDateString", payloadErr?.data, true);
      }
      else {
        setGlobalErrorStatus({
          dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg:
              payloadErr?.data?.globalErrorMessageList?.[0] ??
              ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: false,
          },
          redirect: { redirect: false, redirectFirst: false, path: "", data: {} },
          error,
        });
      }
    }
  };
  
  const PSOIdTitleName = (row) => {
    return row.rowName.orgName ? row.rowName.orgName : null;
  };
  
  const getListCols = (selected, sortingInfo) => {
    const cols = [];
    cols.push(Datatable_Column({
      name: "PSO ID",
      columnName: "psoId",
      selectedRows: selected,
      sortable: true,
      sortFieldNum: 0,
      sortingInfo,
      title: PSOIdTitleName
    }));
    //cols.push(Datatable_Column({
    //name: "PSO Name", columnName: "orgName", selectedRows: selected, sortable: true, sortFieldNum: 1, sortingInfo }));
    cols.push(Datatable_Column({
      name: "PSO Status", columnName: "orgStatus", selectedRows: selected, sortable: true, sortFieldNum: 2, sortingInfo
    }));
    cols.push(Datatable_Column({
      name: "Has PSE Data",
      columnName: "orgId",
      selectedRows: selected,
      sortable: false,
      sortFieldNum: 3,
      sortingInfo,
      formatFunction: (x) => (x ? "Yes" : "No")
    }));
    cols.push(Datatable_Column({
      name: "Status", columnName: "status", selectedRows: selected, sortable: true, sortFieldNum: 4, sortingInfo
    }));
    cols.push(Datatable_Column({
      name: "Processed By", columnName: "userName", selectedRows: selected, sortable: true, sortFieldNum: 5, sortingInfo
    }));
    cols.push(Datatable_Column({
      name: "Processed Date",
      columnName: "processDate",
      selectedRows: selected,
      sortable: true,
      sortFieldNum: 6,
      sortingInfo,
      formatFunction: handleProcessedDateFormat
    }));
    cols.push(Datatable_Column({
      name: "Delisting Date",
      columnName: "delistedDate",
      selectedRows: selected,
      sortable: true,
      sortFieldNum: 7,
      sortingInfo,
      formatFunction: showDelistedDate
    }));
    cols.push(Datatable_Column({
      name: "Delist PSO",
      columnName: "delistedId",
      selectedRows: selected,
      sortable: false,
      sortFieldNum: 8,
      formatFunction: showDelistAction,
      sortingInfo: sortingInfo
    }));
    return cols;
  };
  
  return (
    <>
      {filter && (
        <DatatableBase
          tableClassName="delistedPsoTable"
          uniqueId="delistedPsoTable"
          filter={filter}
          setFilter={setFilter}
          getData={getData}
          getListCols={getListCols}
          hasSelectableRows={false}
          datatableHeader={null}
          datatableDescription={null}
          sortingColumn={sortColumn.sortField}
          sortingOrder={sortColumn.sortDirection}
          searchFieldText={"Search by PSO ID"}
          reload={refreshTableData}
          setReload={setRefreshTableData}
        />
      )}
    </>
  );
};

export default DelistedPSOTable;
